import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingSocialIcons from '../../components/base/FloatingSocialIcons';
import VideoBackground from '../../components/base/VideoBackground';

const ServicesPage = () => {
  const services = [
    {
      icon: 'ri-palette-line',
      title: 'Website Design',
      description: 'Custom, responsive websites that convert visitors into customers. We create stunning designs optimized for user experience and search engines.',
      features: ['Mobile-responsive design', 'SEO-optimized structure', 'Fast loading speeds', 'Conversion-focused layouts'],
      link: '/services/web-design'
    },
    {
      icon: 'ri-article-line',
      title: 'Content Marketing',
      description: 'Strategic content creation that engages your audience and drives results. From blog posts to social media, we craft content that tells your story.',
      features: ['Blog writing & management', 'Social media content', 'Email campaigns', 'Content strategy planning'],
      link: '/services/content'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Local SEO',
      description: 'Dominate local search results and attract nearby customers. We optimize your online presence to ensure you\'re found by people in your area.',
      features: ['Google My Business optimization', 'Local citation building', 'Review management', 'Local keyword targeting'],
      link: '/services/local-seo'
    },
    {
      icon: 'ri-share-line',
      title: 'Social Media Content',
      description: 'Engaging social media content that builds your brand and connects with your audience across all platforms.',
      features: ['Content creation', 'Platform management', 'Engagement strategies', 'Analytics tracking'],
      link: '/services/social-media-content'
    },
    {
      icon: 'ri-pencil-ruler-2-line',
      title: 'Design Services',
      description: 'Professional design solutions that make your brand stand out and leave a lasting impression.',
      features: ['Brand identity', 'Graphic design', 'Visual content', 'Marketing materials'],
      link: '/services/design'
    },
    {
      icon: 'ri-search-line',
      title: 'SEO Services',
      description: 'Comprehensive SEO strategies to improve your search rankings and drive organic traffic to your website.',
      features: ['Keyword research', 'On-page optimization', 'Link building', 'Technical SEO'],
      link: '/services/seo'
    },
    {
      icon: 'ri-google-fill',
      title: 'Google My Business',
      description: 'Optimize your Google My Business profile to attract more local customers and improve your online visibility.',
      features: ['Profile optimization', 'Review management', 'Post scheduling', 'Insights tracking'],
      link: '/services/google-my-business'
    },
    {
      icon: 'ri-chat-smile-3-line',
      title: 'Social Media Management',
      description: 'Full-service social media management to grow your online presence and engage with your audience.',
      features: ['Content scheduling', 'Community management', 'Growth strategies', 'Performance reporting'],
      link: '/services/social-media'
    },
    {
      icon: 'ri-megaphone-line',
      title: 'Digital Marketing',
      description: 'Comprehensive digital marketing strategies to reach your target audience and achieve your business goals.',
      features: ['Marketing strategy', 'Campaign management', 'Multi-channel approach', 'ROI optimization'],
      link: '/services/digital-marketing'
    },
    {
      icon: 'ri-camera-3-line',
      title: 'Virtual Tours',
      description: 'Immersive 360° virtual tours that showcase your business and give customers an inside look.',
      features: ['360° photography', 'Interactive tours', 'Google integration', 'Mobile-friendly'],
      link: '/services/virtual-tours'
    },
    {
      icon: 'ri-advertisement-line',
      title: 'PPC Campaigns',
      description: 'Strategic pay-per-click advertising campaigns that deliver measurable results and maximize your ROI.',
      features: ['Campaign setup', 'Ad creation', 'Budget optimization', 'Performance tracking'],
      link: '/services/ppc-campaigns'
    },
    {
      icon: 'ri-star-line',
      title: 'Reputation Management',
      description: 'Protect and enhance your online reputation with proactive monitoring and review management.',
      features: ['Review monitoring', 'Response management', 'Reputation repair', 'Brand protection'],
      link: '/services/reputation'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Reporting & Analytics',
      description: 'Detailed analytics and reporting to track your marketing performance and make data-driven decisions.',
      features: ['Custom dashboards', 'Performance metrics', 'Trend analysis', 'Actionable insights'],
      link: '/services/reporting-analytics'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security Services',
      description: 'Comprehensive security solutions to protect your website and customer data from threats.',
      features: ['Security audits', 'SSL certificates', 'Malware protection', 'Regular updates'],
      link: '/services/security'
    },
    {
      icon: 'ri-building-line',
      title: 'Business Listings',
      description: 'Get your business listed on top directories and platforms to increase your online visibility.',
      features: ['Directory submissions', 'NAP consistency', 'Citation building', 'Listing optimization'],
      link: '/services/business-listings'
    },
    {
      icon: 'ri-google-line',
      title: 'Google My Business Management',
      description: 'Complete GMB management to maximize your local search presence and attract more customers.',
      features: ['Profile management', 'Post creation', 'Q&A monitoring', 'Photo updates'],
      link: '/services/google-my-business-management'
    },
    {
      icon: 'ri-funds-line',
      title: 'PPC Ad Campaign Management',
      description: 'Expert management of your PPC campaigns across Google Ads, Facebook, and other platforms.',
      features: ['Campaign optimization', 'A/B testing', 'Bid management', 'Conversion tracking'],
      link: '/services/ppc-ad-campaign-management'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 relative overflow-hidden">
      <VideoBackground />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Our Services
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Comprehensive digital marketing solutions designed to help small businesses thrive online. From website design to local SEO, we've got you covered.
            </p>
            <Link
              to="/consultation"
              className="inline-block bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Started Today
            </Link>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-2xl mb-6">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {service.link && (
                    <Link
                      to={service.link}
                      className="inline-block bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-full font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Learn More
                    </Link>
                  )}
                </div>
              ))}
            </div>
            
            {/* FREE CONSULTATION CTA */}
            <div className="text-center mt-12">
              <Link
                to="/consultation"
                className="inline-block bg-gradient-to-r from-green-500 to-emerald-600 text-white px-10 py-5 rounded-full font-bold text-xl hover:shadow-2xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                FREE CONSULTATION
              </Link>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 px-6 bg-white/60 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Our Process
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Discovery', desc: 'We learn about your business, goals, and target audience' },
                { step: '02', title: 'Strategy', desc: 'We create a customized content strategy for your brand' },
                { step: '03', title: 'Creation', desc: 'We produce high-quality content that resonates' },
                { step: '04', title: 'Growth', desc: 'We optimize and scale your social media presence' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-6xl font-bold text-blue-200 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold mb-2 text-gray-800">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries We Serve Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 bg-clip-text text-transparent">
                  Industries We Serve
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized digital marketing solutions tailored to your industry's unique needs and challenges.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Wedding Consultants & Venues */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/5f588427a2c09f46518174e762ab4dc1.jpg"
                    alt="Wedding Consultants & Venues"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-heart-3-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Wedding Consultants & Venues</h3>
                  <p className="text-gray-600 mb-4">
                    Showcase your stunning venues and services with beautiful galleries, booking systems, and targeted local SEO to attract engaged couples.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-pink-500 mr-2 mt-1"></i>
                      <span>Virtual venue tours & photo galleries</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-pink-500 mr-2 mt-1"></i>
                      <span>Online booking & consultation scheduling</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-pink-500 mr-2 mt-1"></i>
                      <span>Wedding-focused social media content</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-pink-500 mr-2 mt-1"></i>
                      <span>Local SEO for "wedding venues near me"</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Restaurants & Caterers */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=upscale%20restaurant%20interior%20with%20elegant%20dining%20setup%2C%20professional%20catering%20service%20with%20gourmet%20food%20presentation%2C%20modern%20restaurant%20ambiance%20with%20stylish%20decor%2C%20culinary%20excellence%20and%20fine%20dining%20experience&width=400&height=300&seq=restaurant1&orientation=landscape"
                    alt="Restaurants & Caterers"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-restaurant-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Restaurants & Caterers</h3>
                  <p className="text-gray-600 mb-4">
                    Drive more reservations and catering orders with mouth-watering content, online ordering integration, and Google My Business optimization.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 mr-2 mt-1"></i>
                      <span>Menu showcase & online ordering systems</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 mr-2 mt-1"></i>
                      <span>Food photography & social media marketing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 mr-2 mt-1"></i>
                      <span>Google My Business & review management</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-orange-500 mr-2 mt-1"></i>
                      <span>Email campaigns for special events</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Real Estate Agents & Brokers */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=luxury%20real%20estate%20property%20showcase%2C%20modern%20home%20exterior%20with%20professional%20real%20estate%20photography%2C%20real%20estate%20agent%20presenting%20beautiful%20house%2C%20upscale%20residential%20property%20with%20stunning%20architecture&width=400&height=300&seq=realestate1&orientation=landscape"
                    alt="Real Estate Agents & Brokers"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-home-smile-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Real Estate Agents & Brokers</h3>
                  <p className="text-gray-600 mb-4">
                    Generate quality leads with property showcase websites, virtual tours, and hyper-local SEO targeting buyers and sellers in your area.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-500 mr-2 mt-1"></i>
                      <span>Property listing websites with IDX integration</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-500 mr-2 mt-1"></i>
                      <span>Virtual tours & 3D property walkthroughs</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-500 mr-2 mt-1"></i>
                      <span>Neighborhood-specific SEO campaigns</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-500 mr-2 mt-1"></i>
                      <span>Lead generation & CRM integration</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Contractors & Freelancers */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20contractor%20working%20on%20construction%20project%2C%20skilled%20freelancer%20in%20modern%20workspace%2C%20construction%20industry%20professional%20with%20tools%20and%20equipment%2C%20quality%20craftsmanship%20and%20professional%20services&width=400&height=300&seq=contractor1&orientation=landscape"
                    alt="Contractors & Freelancers"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-tools-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Contractors & Freelancers</h3>
                  <p className="text-gray-600 mb-4">
                    Build trust and attract clients with professional portfolios, testimonials, and local search optimization that puts you ahead of competitors.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                      <span>Portfolio websites showcasing your work</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                      <span>Before/after project galleries</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                      <span>Quote request & project management tools</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-yellow-600 mr-2 mt-1"></i>
                      <span>Local citation building & review generation</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Home Repair Industry & Services */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="h-48 w-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=home%20repair%20service%20professional%20fixing%20residential%20property%2C%20plumber%20electrician%20HVAC%20technician%20at%20work%2C%20quality%20home%20maintenance%20and%20repair%20services%2C%20trusted%20local%20home%20service%20provider&width=400&height=300&seq=homerepair1&orientation=landscape"
                    alt="Home Repair Industry & Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-hammer-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Home Repair Industry & Services</h3>
                  <p className="text-gray-600 mb-4">
                    Get more emergency calls and scheduled appointments with mobile-optimized websites, click-to-call features, and dominant local search presence.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-1"></i>
                      <span>Mobile-first websites with click-to-call</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-1"></i>
                      <span>Emergency service landing pages</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-1"></i>
                      <span>Service area SEO optimization</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-1"></i>
                      <span>Online scheduling & appointment booking</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* All Industries CTA Card */}
              <div className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex items-center justify-center p-8">
                <div className="text-center text-white">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-briefcase-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Your Industry Here</h3>
                  <p className="text-white/90 mb-6">
                    Don't see your industry listed? We work with businesses across all sectors to deliver customized digital marketing solutions.
                  </p>
                  <Link
                    to="/consultation"
                    className="inline-block bg-white text-red-600 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Let's Talk About Your Business
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Transform Your Social Media?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Let's discuss how our services can help your business grow. Book a free consultation today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/consultation"
                className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Free Consultation
              </Link>
              <Link
                to="/quote"
                className="bg-cyan-400 text-blue-800 px-8 py-4 rounded-full font-bold text-lg hover:bg-cyan-300 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Request a Quote
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;
